import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Row,
  Col,
  Divider,
  Tooltip,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  ReloadOutlined,
  UserOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { userAPI, clientAPI, roleAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import AppHeader from '../components/Header';
import LoadingAnimation from '../components/LoadingAnimation';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  client_id?: string;
  created_at: string;
  updated_at: string;
  invited_by?: string;
  invitation_sent_at?: string;
  permissions?: string[];
}

interface Client {
  id: string;
  name: string;
  status: string;
}

interface Role {
  id: string;
  role_name: string;
  name: string;
  description: string;
  permissions: string[];
  color: string;
  is_system: boolean;
}

const UserManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filters, setFilters] = useState({
    email: '',
    role: '',
    status: '',
    client_id: '',
  });

  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const loadingRef = useRef({ users: false, clients: false, roles: false });

  // Get role options from the roles state - use role_name (actual role identifier)
  const getRoleOptions = () => {
    const options = roles.map(role => ({
      value: role.role_name,
      label: role.role_name, // Use actual role name like "admin", "viewer", etc.
      color: role.color,
      is_system: role.is_system,
    }));
    console.log('Role options:', options);
    return options;
  };

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'suspended', label: 'Suspended', color: 'orange' },
    { value: 'inactive', label: 'Inactive', color: 'red' },
  ];
  const fetchUsers = async () => {
    if (loadingRef.current.users) return;
    
    try {
      loadingRef.current.users = true;
      setLoading(true);
      const response = await userAPI.getUsers(filters);
      setUsers(response.data.users || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
      loadingRef.current.users = false;
    }
  };

  const fetchClients = async () => {
    if (loadingRef.current.clients) return;
    
    try {
      loadingRef.current.clients = true;
      if (user?.role === 'super_admin') {
        const response = await clientAPI.getClients();
        setClients(response.data.clients || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch clients:', error);
    } finally {
      loadingRef.current.clients = false;
    }
  };

  const fetchRoles = async () => {
    if (loadingRef.current.roles) return;
    
    try {
      loadingRef.current.roles = true;
      const response = await roleAPI.getRoles();
      if (response.data.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch roles:', error);
    } finally {
      loadingRef.current.roles = false;
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchClients();
    fetchRoles();
  }, [filters]);

  const handleCreateUser = async (values: any) => {
    try {
      // Debug: Log what we're sending
      console.log('Create user values:', values);
      console.log('Available roles:', roles);
      const selectedRole = roles.find(r => r.role_name === values.role);
      console.log('Selected role details:', selectedRole);
      
      await userAPI.createUser(values);
      message.success('User created successfully');
      setModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error: any) {
      console.error('Create user error:', error);
      message.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (values: any) => {
    if (!editingUser) return;

    try {
      await userAPI.updateUser(editingUser.id, values);
      message.success('User updated successfully');
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleInviteUser = async (values: any) => {
    try {
      setInviteLoading(true);
      
      // Debug: Log what we're sending
      console.log('Invite user values:', values);
      console.log('Available roles:', roles);
      const selectedRole = roles.find(r => r.role_name === values.role);
      console.log('Selected role details:', selectedRole);
      
      const response = await userAPI.inviteUser(values);
      
      // Check if email was sent successfully
      if (response.data.email_sent === false) {
        message.warning(response.data.message || 'Invitation created but email failed to send');
      } else {
        message.success('Invitation sent successfully');
      }
      
      setInviteModalVisible(false);
      inviteForm.resetFields();
      fetchUsers();
    } catch (error: any) {
      console.error('Invite user error:', error);
      message.error(error.response?.data?.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await userAPI.deleteUser(userId);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      role: user.role,
      status: user.status,
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: User) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (roleId: string) => {
        const role = roles.find(r => r.role_name === roleId);
        return (
          <Space>
            {role && (
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: role.color,
                  display: 'inline-block',
                }}
              />
            )}
            <Tag color={role?.color || 'blue'}>
              {roleId.toUpperCase()}
            </Tag>
            {role?.is_system && (
              <Text type="secondary" style={{ fontSize: '11px' }}>
                (System)
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = statusOptions.find(s => s.value === status);
        return (
          <Tag color={statusConfig?.color || 'default'}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => {
        // Hide edit/delete actions for admin and super_admin roles
        const isProtectedRole = record.role === 'admin' || record.role === 'super_admin';
        
        if (isProtectedRole) {
          return <Text type="secondary">-</Text>;
        }
        
        return (
          <Space>
            <Tooltip title="Edit User">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDeleteUser(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete User">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Layout className="layout-container">
      <AppHeader />
      <Content style={{ padding: '24px', minHeight: 'calc(100vh - 64px)' }}>
        <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space align="center">
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard')}
                type="text"
                size="large"
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  User Management
                </Title>
                <Text type="secondary">
                  Manage users and team members for your organization
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={() => setInviteModalVisible(true)}
              >
                Invite User
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Create User
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Input
              placeholder="Search by email"
              prefix={<SearchOutlined />}
              value={filters.email}
              onChange={(e) => setFilters({ ...filters, email: e.target.value })}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by role"
              style={{ width: '100%' }}
              value={filters.role || undefined}
              onChange={(value) => setFilters({ ...filters, role: value || '' })}
              allowClear
            >
              {getRoleOptions().map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              value={filters.status || undefined}
              onChange={(value) => setFilters({ ...filters, status: value || '' })}
              allowClear
            >
              {statusOptions.map(status => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Col>
          {user?.role === 'super_admin' && (
            <Col span={6}>
              <Select
                placeholder="Filter by client"
                style={{ width: '100%' }}
                value={filters.client_id || undefined}
                onChange={(value) => setFilters({ ...filters, client_id: value || '' })}
                allowClear
              >
                {clients.map(client => (
                  <Option key={client.id} value={client.id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}
        </Row>

        <Table
          dataSource={users}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} users`,
          }}
        />
      </Card>

      {/* Create/Edit User Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Create New User'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingUser ? handleUpdateUser : handleCreateUser}
        >
          {!editingUser && (
            <>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' },
                ]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[
                  { required: true, message: 'Please enter password' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                ]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            </>
          )}
          
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter full name' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Select role">
              {getRoleOptions().map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {editingUser && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                {statusOptions.map(status => (
                  <Option key={status.value} value={status.value}>
                    {status.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {user?.role === 'super_admin' && !editingUser && (
            <Form.Item
              name="client_id"
              label="Client"
              rules={[{ required: true, message: 'Please select client' }]}
            >
              <Select placeholder="Select client">
                {clients.map(client => (
                  <Option key={client.id} value={client.id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Invite User Modal */}
      <Modal
        title="Invite New User"
        open={inviteModalVisible}
        onCancel={() => {
          if (!inviteLoading) {
            setInviteModalVisible(false);
            inviteForm.resetFields();
          }
        }}
        footer={null}
        width={500}
        closable={!inviteLoading}
        maskClosable={!inviteLoading}
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInviteUser}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Full Name"
          >
            <Input placeholder="John Doe (optional)" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select placeholder="Select role">
              {getRoleOptions().map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {user?.role === 'super_admin' && (
            <Form.Item
              name="client_id"
              label="Client"
              rules={[{ required: true, message: 'Please select client' }]}
            >
              <Select placeholder="Select client">
                {clients.map(client => (
                  <Option key={client.id} value={client.id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button 
                onClick={() => setInviteModalVisible(false)}
                disabled={inviteLoading}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<MailOutlined />}
                loading={inviteLoading}
              >
                {inviteLoading ? 'Sending Invitation...' : 'Send Invitation'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      </Content>
    </Layout>
  );
};

export default UserManagementPage;
